import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Heart, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { track, markSignupTracked } from '@/lib/analytics';

const COOLDOWN_SECONDS = 60;

export default function VerifyOtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const email = (location.state as any)?.email ?? '';
  const returnTo = (location.state as any)?.returnTo ?? '/app';
  const name = (location.state as any)?.name ?? '';
  const audience = (location.state as any)?.audience ?? 'b2c';

  const [otp, setOtp] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/auth', { replace: true });
    }
  }, [email, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerify = useCallback(async () => {
    if (otp.length !== 6) return;

    setIsVerifying(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: 'signup',
    });
    setIsVerifying(false);

    if (error) {
      toast.error(t('auth.otpInvalid'));
      setOtp('');
    } else {
      track('email_verified', { method: 'otp', audience });
      track('signup_completed', { method: 'email', source: 'client', audience });
      // Prevent the AuthContext catch-all from firing a duplicate.
      if (data?.user?.id) markSignupTracked(data.user.id);

      track('onboarding_step_completed', { step: 'email_confirmation', step_index: 3, flow: 'signup', method: 'email', audience });
      toast.success(t('auth.otpVerified'));
      navigate(returnTo, { replace: true });
    }
  }, [otp, email, returnTo, navigate, t, audience]);

  const handleResend = async () => {
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(t('auth.otpSent'));
      setCooldown(COOLDOWN_SECONDS);
    }
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6) {
      handleVerify();
    }
  }, [otp, handleVerify]);

  const isMicrosoftInbox = /@(hotmail|outlook|live|msn)\./i.test(email);

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center">
              <Heart className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">{t('auth.verifyEmail')}</CardTitle>
          <CardDescription className="space-y-2">
            <p>{t('auth.otpSentTo')}</p>
            <div className="flex items-center justify-center gap-2 text-foreground font-medium">
              <Mail className="w-4 h-4" />
              <span>{email}</span>
            </div>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otp} onChange={setOtp} disabled={isVerifying}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <div className="mx-2" />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            className="w-full"
            onClick={handleVerify}
            disabled={otp.length !== 6 || isVerifying}
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('auth.verifyButton')
            )}
          </Button>

          <div className="rounded-md border bg-muted/50 p-3 space-y-1">
            <p className="text-xs text-muted-foreground">{t('auth.otpSpamHint')}</p>
            {isMicrosoftInbox && (
              <p className="text-xs text-warning">{t('auth.otpOutlookHint')}</p>
            )}
          </div>

          <div className="text-center">
            {cooldown > 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('auth.resendIn', { seconds: cooldown })}
              </p>
            ) : (
              <Button variant="link" onClick={handleResend} className="text-sm">
                {t('auth.resendCode')}
              </Button>
            )}
          </div>

          <div className="text-center">
            <Button variant="ghost" size="sm" onClick={() => navigate('/auth', { replace: true })}>
              ← {t('common.back')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
